/** Mensaje del hilo demo (misma forma que en DemoChatInterface). */
export type DemoChatMessage = {
  id: string;
  role: 'visitor' | 'agent';
  text: string;
  timestamp: Date;
  attachments?: Array<{ kind: string; mimeType: string; caption?: string; url: string }>;
  audioTranscription?: string;
  transcriptionStatus?: string;
};

export type DemoSyncedPayload = {
  id?: string;
  role: 'visitor' | 'agent';
  text: string;
  timestamp: string;
  attachments?: Array<{ kind: string; mimeType: string; caption?: string; url: string }>;
  audioTranscription?: string;
  transcriptionStatus?: string;
};

export type DemoThreadFetchResult = {
  messages: DemoSyncedPayload[];
  operatorTyping: boolean;
};

export async function fetchDemoThreadFromServer(
  token: string,
  visitorId: string
): Promise<DemoThreadFetchResult> {
  const url = `/demo/${encodeURIComponent(token)}/messages?visitorId=${encodeURIComponent(visitorId)}`;
  const r = await fetch(url);
  const data = (await r.json()) as {
    success?: boolean;
    data?: { messages?: DemoSyncedPayload[]; operatorTyping?: boolean };
  };
  if (!data.success || !data.data) {
    return { messages: [], operatorTyping: false };
  }
  const messages = Array.isArray(data.data.messages) ? data.data.messages : [];
  return {
    messages,
    operatorTyping: Boolean(data.data.operatorTyping),
  };
}

export function mapServerPayloadToChatMessages(server: DemoSyncedPayload[]): DemoChatMessage[] {
  return server.map((m, i) => ({
    id: m.id ? `db:${m.id}` : `srv-${m.timestamp}-${i}-${m.role}`,
    role: m.role,
    text: m.text,
    timestamp: new Date(m.timestamp),
    attachments: m.attachments,
    audioTranscription: m.audioTranscription,
    transcriptionStatus: m.transcriptionStatus,
  }));
}

function sameTurn(a: DemoChatMessage, b: DemoChatMessage): boolean {
  return a.role === b.role && a.text === b.text && a.timestamp.getTime() === b.timestamp.getTime();
}

/**
 * Prioriza el servidor cuando trae más mensajes, cierra un turno que quedó colgado
 * o ya no incluye un mensaje borrado.
 */
export function reconcileDemoMessages(local: DemoChatMessage[], server: DemoChatMessage[]): DemoChatMessage[] {
  if (!server.length) return local;
  if (!local.length) return server;

  if (server.length > local.length) return server;
  if (server.length < local.length) {
    const serverIds = new Set(server.map((m) => m.id));
    const missingPersisted = local.some((m) => m.id.startsWith('db:') && !serverIds.has(m.id));
    if (!missingPersisted) return local;
    const optimisticTail = local.filter((m) => !m.id.startsWith('db:'));
    return [...server, ...optimisticTail];
  }

  if (local.every((m, i) => sameTurn(m, server[i]!))) return server;

  const lLast = local[local.length - 1]!;
  const sLast = server[local.length - 1]!;
  if (lLast.role === 'visitor' && sLast.role === 'agent') return server;
  if (lLast.role === 'agent' && sLast.role === 'agent') {
    if (lLast.text !== sLast.text) return server;
    const la = JSON.stringify(lLast.attachments ?? null);
    const sa = JSON.stringify(sLast.attachments ?? null);
    if (la !== sa) return server;
  }
  return local;
}
